import { createApp } from 'vue';
import { createPinia } from 'pinia';

import App from './App.vue';
import router from './router';

// Element Plus 按需引入（audit P-1/P-2 修复，2026-08-13）：
// - 模板组件 <el-button> 等由 unplugin-vue-components 自动 import + 对应 CSS
// - 命令式 API（ElMessage / ElMessageBox）由调用文件显式 import 'element-plus'
// - 不再需要 app.use(ElementPlus) + 全量 CSS import
// EP 主题覆盖：必须在 element-plus 组件 CSS 之后 import 以保证优先级
// （unplugin 自动注入的 CSS 在 build 时按组件粒度加载，主题覆盖需保持源代码顺序）
import '@/styles/element-overrides.css';
// Portal 视觉 Token（颜色 / 圆角 / 间距 / 字号）
import '@/styles/portal-tokens.css';
// Portal 全局基础样式
import '@/styles/base.css';
// Markdown 渲染样式（来源 base.css:366-391 .md 全局类）
import '@/styles/md.css';

// EP 基础 CSS（reset + 主题变量）：按需引入时仍需要这一行作为基底
import 'element-plus/theme-chalk/base.css';

// 命令式 API 样式（2026-08-16 修复「ElMessageBox 渲染在页面底部而非居中弹窗」）：
// unplugin 按需引入只给模板组件注入 CSS；ElMessage / ElMessageBox 是函数式调用，
// 显式 `import { ElMessageBox } from 'element-plus'` 只带 JS 不带样式，
// 缺样式时弹窗 DOM 按文档流渲染到 body 末尾（表现为「提示出现在页面最下面」）。
// 此处统一引入两个函数式组件的样式入口（各 ~3KB，官方按需引入推荐写法）。
import 'element-plus/es/components/message/style/css';
import 'element-plus/es/components/message-box/style/css';

import { useAuthStore } from '@/stores/auth';
import { applyFrameBust, enforceHttpsInProd, isSafeRedirect } from '@/utils/security';

// P-007 安全加固：HTTPS 强制 + Frame bust 必须在 app.mount 之前执行，
// 避免被嵌入 iframe 或 HTTP 协议下渲染任何内容（防止早期 clickjacking / MITM）。
enforceHttpsInProd();
applyFrameBust();

const app = createApp(App);
const pinia = createPinia();
app.use(pinia);
app.use(router);
// Element Plus 不再 app.use（按需引入，组件由 unplugin 自动注册）

/**
 * 多 tab 鉴权状态同步（P-007 安全加固）。
 *
 * 触发条件：同源不同 tab 的 localStorage 写入会触发本 tab 的 storage event。
 * sessionStorage 是 tab 隔离的，无法直接监听其他 tab 的鉴权状态变更，
 * 因此借助 localStorage 哨兵 key `wfb:auth-event` 实现跨 tab 通知。
 *
 * 行为：
 * - 收到 'logout' 事件：本 tab 也立即清 token + 跳登录页
 *   （其他 tab 已登出，本 tab 不应继续保留登录态）
 * - 收到 'login' 事件：不动作（避免覆盖本 tab 的当前操作）
 *   （若用户在 tab A 登录、tab B 未登录，tab B 仍需用户主动操作才登录，
 *    这是 sessionStorage tab 隔离设计的预期行为）
 *
 * 监听注册时机：在 bootstrap 之外同步注册（不阻塞 mount），避免错过早期事件。
 *
 * audit R-6 防重入（2026-08-13）：HMR 多次 bootstrap 时会重复注册 storage listener，
 * 旧 listener 引用旧 router 实例 → 触发时跳错路径。用 window 上的 flag 兜底：
 * 已注册过则跳过（HMR 重载时新 listener 引用新 router，旧的不会被覆盖）。
 *
 * audit S-1 加固：router.replace 前用 isSafeRedirect 校验 currentPath，
 * 不通过则 fallback 到对应登录页根路径（防 URL 注入跨路径跳转）。
 */
declare global {
  interface Window {
    __portalStorageBound?: boolean;
  }
}
if (!window.__portalStorageBound) {
  window.__portalStorageBound = true;
  window.addEventListener('storage', (e) => {
    if (e.key !== 'wfb:auth-event') return;
    const newValue = e.newValue || '';
    // 哨兵值格式：`${type}:${timestamp}`，type ∈ {login, logout}
    if (!newValue.startsWith('logout:')) return;
    // 收到登出广播：清本 tab 状态 + 跳登录页。
    // 注意：必须 pinia 已注册才能 useAuthStore，此处延迟到事件触发时调用
    // （事件触发时 app 已经 mount 完成，pinia 必然就绪）。
    const auth = useAuthStore();
    if (!auth.isLoggedIn) return;
    auth.clear();
    // 跳对应登录页（admin 区域 → /admin/login，其他 → /login）
    const currentPath = router.currentRoute.value.fullPath;
    const isAdminArea = currentPath.startsWith('/admin');
    const loginPath = isAdminArea ? '/admin/login' : '/login';
    // S-1：redirect 校验，防 URL 注入跨路径
    const safeRedirect = isSafeRedirect(currentPath) ? currentPath : loginPath;
    void router.replace({
      path: loginPath,
      query: { redirect: safeRedirect },
    });
  });
}

/**
 * 启动时拉一次 /api/auth/me 刷新 user（仅当本地存有 token）。
 *
 * 设计要点（与路由守卫的时序协同）：
 * 1. Pinia 必须先注册（pinia 装在 app 上之后，useAuthStore 才能拿到实例）
 * 2. router.beforeEach 中调 useAuthStore 时，会复用同一个 pinia 实例
 * 3. 这里 fetchMe 是 await 的，确保首次进入受保护路由前 user 已就绪
 *    —— 避免用户刷新 /wb/home 时因 store.user 还没拉到而误判为未登录
 * 4. fetchMe 失败（401）由 http.ts 拦截器统一处理（clear + 跳登录），此处仅 catch 日志
 *
 * 老前端对应逻辑：auth-main.js handleOAuthCallbackOnMain 之后会拉一次 me 同步状态
 */
async function bootstrap() {
  const auth = useAuthStore();
  if (auth.token) {
    try {
      await auth.fetchMe();
    } catch (err) {
      // fetchMe 失败不阻塞挂载（http.ts 拦截器已处理 clear + 跳登录）
      console.warn('[bootstrap] fetchMe failed:', err);
    }
  }
  app.mount('#app');
}

void bootstrap();
